// src/utils/whatsapp.js
// Lien de contact WhatsApp (https://wa.me/<numéro>) depuis un profil membre.
//
// wa.me exige un numéro INTERNATIONAL en chiffres purs : pas de « + », pas
// d'espaces, pas de zéro de tête national (33612345678, pas 06 12 34 56 78).
// Les téléphones sont en saisie libre (imports XLS, formulaires) : la
// normalisation est donc volontairement CONSERVATRICE. Un bouton absent est
// préférable à un lien qui ouvre la conversation d'un inconnu ; au moindre
// doute on renvoie null.
//
// Règle centrale : AUCUN pays par défaut. Un numéro national (« 06 12 … »)
// n'est interprété qu'avec l'indicatif du pays du membre.
import countries from 'world-countries'
import { countryNameToCode } from './countryCode'

// E.164 : 15 chiffres max, indicatif inclus. En dessous de 8 ce n'est pas un
// numéro exploitable.
const MIN_DIGITS = 8
const MAX_DIGITS = 15

// Pays où le « 0 » de tête fait partie du numéro (il n'est pas un préfixe de
// trunk) : le retirer casserait le numéro.
const KEEPS_LEADING_ZERO = new Set(['IT', 'VA', 'SM'])

// Indicatif téléphonique par ISO-2. world-countries : idd = { root: '+3',
// suffixes: ['3'] } pour la France. Un seul suffixe → indicatif complet ;
// plusieurs (NANP +1, Russie/Kazakhstan +7) → la racine seule est l'indicatif.
const callingCodeByIso2 = {}
countries.forEach((c) => {
  const root = c.idd?.root
  if (!root) return
  const suffixes = c.idd.suffixes || []
  callingCodeByIso2[c.cca2] = (suffixes.length === 1 ? root + suffixes[0] : root).replace(/\D/g, '')
})

// `member.country` est un nom (« France », « Colombia »). On accepte aussi un
// code ISO-2 déjà normalisé. Renvoie l'indicatif en chiffres, ou null.
const callingCodeForCountry = (country) => {
  if (!country) return null
  const raw = String(country).trim()
  const iso2 = /^[A-Za-z]{2}$/.test(raw) ? raw.toUpperCase() : countryNameToCode(raw)
  return iso2 ? { iso2, code: callingCodeByIso2[iso2] || null } : null
}

/**
 * Normalise un téléphone vers le format `wa.me`, ou null si incertain.
 *
 * @param {string} raw            Numéro tel que saisi.
 * @param {string} [country]      Pays du membre (nom ou ISO-2). Sans lui, seuls
 *                                les numéros explicitement internationaux
 *                                (`+…` ou `00…`) sont acceptés.
 * @param {object} [options]
 * @param {boolean} [options.internationalOnly] N'accepte que `+…`/`00…`.
 * @returns {string|null} Chiffres seuls, ex. « 33612345678 ».
 */
export function toWhatsAppNumber(raw, country, { internationalOnly = false } = {}) {
  if (typeof raw !== 'string') return null
  // Un seul numéro attendu. Deux numéros dans le champ (« / », « ; », « ou »)
  // = ambigu.
  if (/[/;,]|\bou\b|\bor\b/i.test(raw)) return null
  // Uniquement chiffres, espaces et ponctuation courante de numéro : tout autre
  // caractère (lettres, poste « ext », etc.) rend la saisie douteuse.
  if (/[^\d\s+().\-–]/.test(raw)) return null

  let text = raw.trim()
  const explicitInternational = /^(\+|00)/.test(text)

  if (explicitInternational) {
    // « +33 (0)6 12… » : le (0) est un artefact d'affichage, pas un chiffre.
    text = text.replace(/\(0\)/g, '')
    const digits = text.replace(/\D/g, '').replace(/^00/, '')
    if (digits.length < MIN_DIGITS || digits.length > MAX_DIGITS) return null
    // Un indicatif ne commence jamais par 0.
    if (digits.startsWith('0')) return null
    return digits
  }

  if (internationalOnly) return null

  const info = callingCodeForCountry(country)
  if (!info || !info.code) return null

  let national = text.replace(/\D/g, '')
  if (!KEEPS_LEADING_ZERO.has(info.iso2)) national = national.replace(/^0/, '')
  // Après nettoyage : pas de « 00 » résiduel (préfixe international mal formé)
  // ni de numéro trop court pour être un mobile.
  if (!national || national.startsWith('00') || national.length < 6) return null

  const full = info.code + national
  if (full.length < MIN_DIGITS || full.length > MAX_DIGITS) return null
  return full
}

/**
 * URL `wa.me` pour le champ téléphone d'un membre, ou null si le bouton ne
 * doit PAS s'afficher. Concentre les trois gardes en un point testable :
 *  - contact autorisé (même règle que « Envoyer un message ») ;
 *  - champ non privé (`privacy_settings[field] === false` = masqué) ;
 *  - numéro normalisable sans ambiguïté.
 *
 * `mobilePhone` : normalisation complète (pays du membre). `phone` : seulement
 * s'il est explicitement international, un fixe n'ayant pas de WhatsApp.
 */
export function getMemberWhatsAppUrl({ member, field, canContact }) {
  if (!canContact || !member) return null
  if (member.privacy_settings?.[field] === false) return null
  const raw = member[field]
  if (!raw) return null
  const number = toWhatsAppNumber(raw, member.country, {
    internationalOnly: field !== 'mobilePhone',
  })
  return number ? `https://wa.me/${number}` : null
}
